import React, { useContext } from "react";
import { NetworkStatus } from "@apollo/client";
import { RESOURCE_NAME } from "common/authorization";
import {
  ParcelImportTaskType,
  useAccountParcelImportTasksQuery,
  useHasParcelImportTaskInProgressQuery,
} from "../../../../generated/graphql";
import { spacing } from "../../../../stitches.config";
import { AuthContext } from "../../../Authorization/AuthContext";
import { Box } from "../../../Common/__styles__/Layout";
import { Button } from "../../../Common/Button";
import Divider from "../../../Common/Divider";
import { EmptyState } from "../../../Common/EmptyState";
import { Icons } from "../../../Common/Icons";
import { useManualPaginationConfig } from "../../../Common/Tables/hooks";
import { DropdownMenu } from "../../../Inputs";
import { ActionsProps } from "../../../Inputs/DropdownMenu";
import {
  Header,
  HeaderTitle,
  Section,
  SectionTitle,
} from "../../__styles__/Content";
import RecentJobsTable, { RecentJob } from "../RecentJobsTable";
import ParcelImportConfiguration from "./ParcelImportConfiguration";
import { useConfigurationOverridesModal } from "./ParcelImportWizard/ConfigurationOverridesModal";

export const Parcels = () => {
  const { authorized, account } = useContext(AuthContext);
  const manualPaginationConfig = useManualPaginationConfig({ pageSize: 10 });
  const variables = {
    page: manualPaginationConfig.pagination.pageIndex + 1,
    pageSize: manualPaginationConfig.pagination.pageSize,
  };

  const {
    data: currentData,
    networkStatus,
    loading,
    error,
    previousData,
    refetch: refetchAccountParcelImportTasks,
  } = useAccountParcelImportTasksQuery({
    fetchPolicy: "no-cache",
    pollInterval: 10000,
    variables,
  });

  const { data: statusData, refetch: refetchHasParcelImportTaskInProgress } =
    useHasParcelImportTaskInProgressQuery({
      fetchPolicy: "no-cache",
    });

  const refetch = async () => {
    await refetchAccountParcelImportTasks();
    await refetchHasParcelImportTaskInProgress();
  };

  const canCreateParcelConfiguration =
    authorized({
      resource: RESOURCE_NAME.PARCEL_IMPORT_CONFIGURATION,
      permission: "create",
    }) && !statusData?.hasParcelImportTaskInProgress;

  const parcelImportButton = ({
    onClick,
  }: {
    onClick: (_: React.MouseEvent<HTMLElement>) => void;
  }) => (
    <Button
      onClick={onClick}
      styleVariant="outlineLight"
      size="small"
      rightIconName="chevron-down"
    >
      Update parcel source
    </Button>
  );

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  const parcelImportTasks = data?.account?.parcelImportTasks.data;

  const parcelImportConfiguration = data?.account?.parcelImportConfiguration;

  const hasParcelImportTasks =
    parcelImportTasks && parcelImportTasks.length > 0;

  const hasParcelImportConfiguration = !!parcelImportConfiguration;

  const [showConfigOverridesModal] = useConfigurationOverridesModal({
    parcelImportConfiguration,
  });

  const actions: Array<ActionsProps> = [
    {
      label: "ArcGIS rest service import",
      onClick: () => showConfigOverridesModal(ParcelImportTaskType.ARCGIS),
      disabled: !canCreateParcelConfiguration,
    },
    {
      label: "Geopackage import",
      onClick: () => showConfigOverridesModal(ParcelImportTaskType.GPKG),
      disabled: !canCreateParcelConfiguration,
    },
  ];

  if (error) return <div>Error loading parcel imports.</div>;

  return (
    <Box>
      <Header>
        <HeaderTitle>Parcel data settings</HeaderTitle>
        <DropdownMenu actions={actions} customButton={parcelImportButton} />
      </Header>
      <Divider />
      {!loading && (
        <>
          {hasParcelImportConfiguration && (
            <>
              <Section>
                <SectionTitle>Parcel import configuration</SectionTitle>
              </Section>
              <ParcelImportConfiguration
                parcelImportConfiguration={parcelImportConfiguration}
                onUpdate={refetch}
                filename={parcelImportTasks?.[0]?.name}
              />
              {hasParcelImportTasks && <Divider margins="0px 24px" />}
            </>
          )}
          {hasParcelImportTasks && (
            <Section>
              <SectionTitle>Recent imports</SectionTitle>
              <RecentJobsTable
                recentJobs={parcelImportTasks.map(parcelImportTask => {
                  return {
                    id: parcelImportTask.id,
                    name: parcelImportTask.name,
                    error: parcelImportTask.errors ?? undefined,
                    createdAt: parcelImportTask.createdAt,
                    stoppedAt: parcelImportTask.stoppedAt ?? undefined,
                    status: parcelImportTask.status,
                    csvDownloadHref:
                      parcelImportTask.failedProperties.length ?
                        `/api/parcel-import/${parcelImportTask.id}/failed-properties?accountId=${account?.id}`
                      : undefined,
                    icon:
                      (
                        parcelImportTask.importType ===
                        ParcelImportTaskType.GPKG
                      ) ?
                        Icons.GPKG
                      : Icons.ARC_GIS,
                  } satisfies RecentJob;
                })}
                manualPaginationConfig={{
                  ...manualPaginationConfig,
                  pageCount:
                    data.account?.parcelImportTasks.pageInfo.totalPages ?? 1,
                }}
                loading={loading}
                loadingText="Loading recent imports"
                noDataText="No recent imports found"
              />
            </Section>
          )}
          {!hasParcelImportConfiguration && !hasParcelImportTasks && (
            <EmptyState
              icon="add-raster"
              title="No current parcel data source for this account"
              body="Update parcel source to get started."
              paddingTop={spacing["2xl"].value}
              paddingBottom={spacing["2xl"].value}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default Parcels;
